import React, { useState, useEffect } from "react"
import {
  Menu,
  MenuItem,
  FormGroup,
  TextArea,
  Tag,
  RadioGroup,
  Radio,
  InputGroup,
  Callout,
} from "@blueprintjs/core"
import { DotsThreeVertical } from "@phosphor-icons/react/dist/ssr"
import CustomPopover from "components/Atoms/CustomPopover"
import CustomDialog from "components/Atoms/CustomDialog"
import CustomButton from "components/Atoms/CustomButton"
import { useDispatch, useSelector } from "react-redux"
import { PalletStateType, updatePallet } from "store/production.reducer"
import { ApplicationState } from "store"
import CustomTag from "components/Atoms/CustomTag"
import { useAddRemarkProductionItemMutation } from "generated/graphql"
import { RESTOCK_PRODUCTION } from "graphql/production/production.queries.gql"
import ButtonMutation from "components/Molecules/ButtonMutation"
import Divider from "components/Atoms/Divider"
import WidgetLabelPreview, { LabelType } from "./WidgetLabelPreview"

interface PalletActionProps {
  index: number
  remark: string
  production: any
  pallet: PalletStateType
  onRefetch?: () => void
}

export default function PalletAction({
  index,
  remark,
  production,
  pallet,
  onRefetch,
}: PalletActionProps) {
  const dispatch = useDispatch()
  const [isRemarkOpen, setIsRemarkOpen] = useState(false)
  const [isPalletTypeOpen, setIsPalletTypeOpen] = useState(false)
  const [isRejectOpen, setIsRejectOpen] = useState(false)
  const [isReverseStockOpen, setIsReverseStockOpen] = useState(false)
  const [isLabelPreviewOpen, setIsLabelPreviewOpen] = useState(false)
  const [activeLabelType, setActiveLabelType] = useState<LabelType>("box-end")

  const openLabelPreview = (type: LabelType) => {
    setActiveLabelType(type)
    setIsLabelPreviewOpen(true)
  }
  const [rejectInputs, setRejectInputs] = useState<{ [key: string]: number }>(
    {}
  )
  const [remarkText, setRemarkText] = useState("")
  const [selectedRemarks, setSelectedRemarks] = useState<string[]>([])
  const [palletType, setPalletType] = useState<"euro" | "regular">(
    pallet?.isEuro ? "euro" : "regular"
  )
  const [addRemarkProductionItem] = useAddRemarkProductionItemMutation()

  const predefinedRemarks = [
    "Damaged box",
    "Label missing",
    "Weight mismatch",
    "Incorrect pallet number",
    "Seal broken",
  ]

  useEffect(() => {
    if (!remark) {
      setRemarkText("")
      setSelectedRemarks([])
      return
    }

    const lowerRemark = remark.toLowerCase()
    const detected = predefinedRemarks.filter(r =>
      lowerRemark.includes(r.toLowerCase())
    )

    setSelectedRemarks(detected)

    const remainingText = predefinedRemarks.reduce((acc, r) => {
      const pattern = new RegExp(r, "gi")
      return acc.replace(pattern, "")
    }, remark)

    setRemarkText(remainingText.replace(/[,\-]+/g, "").trim())
  }, [remark])

  useEffect(() => {
    if (pallet?.isEuro) setPalletType("euro")
    else setPalletType("regular")
  }, [pallet])

  const toggleRemark = (r: string) => {
    setSelectedRemarks(prev =>
      prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]
    )
  }

  const handleRemarkConfirm = async () => {
    const combinedRemark = [...selectedRemarks, remarkText.trim()]
      .filter(Boolean)
      .join(", ")

    console.log("pallet?.id", pallet?.id)

    dispatch(
      updatePallet(
        {
          ...pallet,
          remark: combinedRemark,
        },
        index
      )
    )

    if (pallet?.id != null) {
      try {
        await addRemarkProductionItem({
          variables: {
            input: {
              lineProductionItemId: pallet.id,
              remark: combinedRemark,
            },
          },
        })
      } catch (error) {
        console.error("Error adding remark:", error)
      }
    }
    setIsRemarkOpen(false)
  }

  const productionState = useSelector(
    (state: ApplicationState) => state.production.production
  )

  const handlePalletTypeConfirm = () => {
    if (!pallet) return

    const palletsCopy: PalletStateType[] = [...(productionState?.pallets ?? [])]

    const currentEuroIndex = palletsCopy.findIndex(p => p.isEuro)

    // Only the pallet type flag changes. Each pallet keeps its own number,
    // MH codes, gate passes and quantities, so nothing gets swapped between pallets.
    if (palletType === "euro") {
      if (currentEuroIndex !== -1 && currentEuroIndex !== index) {
        palletsCopy[currentEuroIndex] = {
          ...palletsCopy[currentEuroIndex],
          isEuro: false,
        }
      }
      palletsCopy[index] = { ...pallet, isEuro: true }
    } else {
      palletsCopy[index] = { ...pallet, isEuro: false }
    }

    // Dispatch updates
    palletsCopy.forEach((p, i) => {
      dispatch(updatePallet(p, i))
    })

    setIsPalletTypeOpen(false)
  }

  const handleRejectConfirm = () => {
    if (!pallet?.gatePasses?.length) return

    const palletsCopy: PalletStateType[] = [...(productionState?.pallets ?? [])]
    const capacity = production?.shipmentPlan?.skuType?.capacity ?? 1

    pallet.gatePasses.forEach(gp => {
      // Get the packed boxes entered by user
      const packedBoxes = rejectInputs[gp.id] ?? 0

      // Calculate total boxes for this gate pass in this pallet
      const totalWeight = gp.weight ?? 0
      const rejectedWeight = gp.rejectedWeight ?? 0
      const totalBoxes =
        capacity > 0 ? (totalWeight + rejectedWeight) / capacity : 0

      // Calculate rejected boxes from packed boxes
      const rejectedBoxes = totalBoxes - packedBoxes
      const newReject = rejectedBoxes * capacity

      // find existing rejection for this gatepass in THIS pallet only
      const currentExistingRejected = gp.rejectedWeight ?? 0

      const diff = newReject - currentExistingRejected
      if (diff === 0) return // nothing to change

      if (diff > 0) {
        // Only apply rejection to the CURRENT pallet (index), not other pallets
        const currentPallet = { ...palletsCopy[index] }
        const gpIndex = currentPallet.gatePasses.findIndex(g => g.id === gp.id)

        if (gpIndex !== -1) {
          const currentGP = { ...currentPallet.gatePasses[gpIndex] }
          const availableWeight = currentGP.weight ?? 0
          const applied = Math.min(diff, availableWeight)
          const capacity = production?.shipmentPlan?.skuType?.capacity ?? 1

          currentGP.weight -= applied
          currentGP.rejectedWeight = (currentGP.rejectedWeight ?? 0) + applied

          // compute equivalent box values
          const appliedBoxes = capacity > 0 ? applied / capacity : 0
          currentGP.boxes -= appliedBoxes
          currentGP.rejectedQuantity =
            (currentGP.rejectedQuantity ?? 0) + appliedBoxes

          currentPallet.gatePasses[gpIndex] = currentGP
          const totalWeight = currentPallet.gatePasses.reduce(
            (sum, g) => sum + (g.weight ?? 0),
            0
          )
          currentPallet.weightCount = totalWeight
          currentPallet.boxCount = capacity > 0 ? totalWeight / capacity : 0

          palletsCopy[index] = currentPallet
        }
      } else {
        // need to REDUCE rejection (undo) - Only affect the CURRENT pallet
        const remainingToRestore = Math.abs(diff)
        const currentPallet = { ...palletsCopy[index] }
        const gpIndex = currentPallet.gatePasses.findIndex(g => g.id === gp.id)

        if (gpIndex !== -1) {
          const currentGP = { ...currentPallet.gatePasses[gpIndex] }
          const capacity = production?.shipmentPlan?.skuType?.capacity ?? 1

          const rejected = currentGP.rejectedWeight ?? 0
          const applied = Math.min(remainingToRestore, rejected)

          // === restore weights and boxes ===
          currentGP.rejectedWeight = rejected - applied
          currentGP.weight = (currentGP.weight ?? 0) + applied

          const restoredBoxes = capacity > 0 ? applied / capacity : 0
          currentGP.rejectedQuantity =
            (currentGP.rejectedQuantity ?? 0) - restoredBoxes
          currentGP.boxes = (currentGP.boxes ?? 0) + restoredBoxes

          currentPallet.gatePasses[gpIndex] = currentGP
          const totalWeight = currentPallet.gatePasses.reduce(
            (sum, g) => sum + (g.weight ?? 0),
            0
          )
          currentPallet.weightCount = totalWeight
          currentPallet.boxCount = capacity > 0 ? totalWeight / capacity : 0

          // Clean up gate passes with zero weight
          currentPallet.gatePasses = currentPallet.gatePasses.filter(
            g => (g.weight ?? 0) > 0 || (g.rejectedWeight ?? 0) > 0
          )

          palletsCopy[index] = currentPallet
        }
      }
    })

    palletsCopy.forEach((p, i) => dispatch(updatePallet(p, i)))

    setRejectInputs({})
    setIsRejectOpen(false)
  }

  useEffect(() => {
    if (!isRejectOpen || !pallet?.gatePasses?.length) return

    const capacity = production?.shipmentPlan?.skuType?.capacity ?? 1
    const initialInputs: { [key: string]: number } = {}

    pallet.gatePasses.forEach(gp => {
      // Calculate total boxes for this gate pass in this pallet
      const totalWeight = gp.weight ?? 0
      const rejectedWeight = gp.rejectedWeight ?? 0
      const totalBoxes =
        capacity > 0 ? (totalWeight + rejectedWeight) / capacity : 0
      const rejectedBoxes = capacity > 0 ? rejectedWeight / capacity : 0

      // Initialize with PACKED boxes (total - rejected)
      const packedBoxes = totalBoxes - rejectedBoxes
      if (gp.id) initialInputs[gp.id] = Number(packedBoxes.toFixed(2))
    })

    setRejectInputs(initialInputs)
  }, [isRejectOpen])

  const isEuroAllowed = (() => {
    try {
      const meta = production?.shipmentPlan?.metadata
        ? JSON.parse(production.shipmentPlan.metadata)
        : {}
      return meta.is_euro === true
    } catch {
      return false
    }
  })()

  const euroQuotaError = (() => {
    if (palletType !== "euro" || !pallet) return null
    let meta: any = {}
    try {
      meta = production?.shipmentPlan?.metadata
        ? JSON.parse(production.shipmentPlan.metadata)
        : {}
    } catch {
      meta = {}
    }
    const euroBoxQuota = meta.euro_pallet_box_count ?? 0
    const capacity = production?.shipmentPlan?.skuType?.capacity ?? 1
    const euroWeightQuota = euroBoxQuota * capacity

    // The pallet that will end up as Euro (existing Euro pallet is swapped in)
    const pallets = productionState?.pallets ?? []
    const existingEuro = pallets.find((p, i) => p.isEuro && i !== index)
    const target = existingEuro ?? pallet

    const mhBoxes = (target.mhCodes ?? []).reduce(
      (sum, mh) => sum + (mh.boxes ?? 0),
      0
    )
    const gpWeight = (target.gatePasses ?? []).reduce(
      (sum, gp) => sum + (gp.weight ?? 0),
      0
    )

    const errors: string[] = []
    if (mhBoxes > euroBoxQuota)
      errors.push(
        `MH codes have ${mhBoxes} boxes assigned, which exceeds the Euro pallet quota of ${euroBoxQuota} boxes.`
      )
    if (gpWeight > euroWeightQuota)
      errors.push(
        `Gate passes have ${gpWeight.toFixed(
          2
        )} kg assigned, which exceeds the Euro pallet quota of ${euroWeightQuota.toFixed(
          2
        )} kg.`
      )
    return errors.length ? errors : null
  })()

  const rejectionStats: Record<
    string,
    { totalWeight: number; totalRejected: number; percent: number }
  > = {}

  ;(productionState?.pallets ?? []).forEach(p => {
    p.gatePasses?.forEach(gp => {
      if (!gp.id) return
      if (!rejectionStats[gp.id]) {
        rejectionStats[gp.id] = { totalWeight: 0, totalRejected: 0, percent: 0 }
      }
      rejectionStats[gp.id].totalWeight += gp.weight ?? 0
      rejectionStats[gp.id].totalRejected += gp.rejectedWeight ?? 0
    })
  })

  Object.keys(rejectionStats).forEach(id => {
    const { totalWeight, totalRejected } = rejectionStats[id]
    const total = totalWeight + totalRejected // original assigned weight before rejection
    rejectionStats[id].percent = total > 0 ? (totalRejected / total) * 100 : 0
  })

  const getPercentColor = (value: number) => {
    if (value > 25) return "text-red-700"
    if (value > 18) return "text-yellow-600"
    if (value > 7) return "text-green-700"
    return "text-orange-600"
  }

  return (
    <>
      <CustomPopover
        content={
          <Menu>
            <MenuItem
              icon="comment"
              className="text-sm"
              text="Remark"
              onClick={() => setIsRemarkOpen(true)}
            />
            {isEuroAllowed && !production?.sendForApproval && (
              <MenuItem
                icon="tag"
                className="text-sm"
                text={"Pallet Type"}
                onClick={() => setIsPalletTypeOpen(true)}
              />
            )}
            <MenuItem
              icon="cross-circle"
              className="text-sm"
              text="Rejection"
              onClick={() => setIsRejectOpen(true)}
            />
            {!production?.sendForApproval &&
              pallet?.id &&
              (pallet?.mhCodes?.length > 0 ||
                pallet?.gatePasses?.length > 0) && (
                <MenuItem
                  icon="undo"
                  className="text-sm"
                  text="Reverse Stock"
                  onClick={() => setIsReverseStockOpen(true)}
                />
              )}

            <Divider />

            {pallet?.lotCode?.length > 0 && (
              <MenuItem
                icon="print"
                className="text-sm"
                text="Print Box End Label"
                onClick={() => openLabelPreview("box-end")}
              />
            )}

            {pallet?.lotCode?.length > 0 && (
              <MenuItem
                icon="print"
                className="text-sm"
                text="Print SSCC Label"
                onClick={() => openLabelPreview("sscc")}
              />
            )}

            {pallet?.lotCode?.length > 0 && (
              <MenuItem
                icon="print"
                className="text-sm"
                text="Print Pallet No Label"
                onClick={() => openLabelPreview("pallet-no")}
              />
            )}
          </Menu>
        }
        position="bottom"
      >
        <DotsThreeVertical size={18} className="cursor-pointer" />
      </CustomPopover>

      {/* === Remark Dialog === */}
      <CustomDialog
        light
        width="35%"
        icon="comment"
        title="Remark"
        isOpen={isRemarkOpen}
        onClose={() => setIsRemarkOpen(false)}
        footerShowCloseButton={false}
        footerRightButton={
          <div className="flex items-center justify-between w-full">
            <CustomButton
              text="Cancel"
              onClick={() => setIsRemarkOpen(false)}
            />
            <CustomButton
              intent="success"
              text="Confirm"
              onClick={handleRemarkConfirm}
            />
          </div>
        }
      >
        <div className="p-4 space-y-4">
          <FormGroup label="Select predefined remarks" className="mb-2">
            <div className="flex flex-wrap gap-2">
              {predefinedRemarks.map((r, i) => {
                const isSelected = selectedRemarks.includes(r)
                return (
                  <Tag
                    key={i}
                    interactive
                    large
                    minimal={!isSelected}
                    intent={isSelected ? "primary" : "none"}
                    onClick={() => toggleRemark(r)}
                  >
                    {r}
                  </Tag>
                )
              })}
            </div>
          </FormGroup>

          <hr className="border-gray-200" />

          <FormGroup
            label="Additional notes"
            helperText="You can add extra details if needed."
          >
            <TextArea
              fill
              rows={4}
              value={remarkText}
              placeholder="Type remark here..."
              onChange={e => setRemarkText(e.target.value)}
            />
          </FormGroup>
        </div>
      </CustomDialog>

      {/* === Pallet Type Dialog === */}
      <CustomDialog
        light
        width="30%"
        icon="tag"
        title="Pallet Type"
        isOpen={isPalletTypeOpen}
        onClose={() => setIsPalletTypeOpen(false)}
        footerShowCloseButton={false}
        footerRightButton={
          <div className="flex items-center justify-between w-full">
            <CustomButton
              text="Cancel"
              onClick={() => setIsPalletTypeOpen(false)}
            />
            <CustomButton
              intent="success"
              text="Confirm"
              disabled={!!euroQuotaError}
              onClick={handlePalletTypeConfirm}
            />
          </div>
        }
      >
        <div className="p-4">
          <FormGroup
            label={<span className="font-600">Select pallet type</span>}
          >
            <RadioGroup
              selectedValue={palletType}
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                setPalletType(e.currentTarget.value as "euro" | "regular")
              }
            >
              <Radio label="Regular Pallet" value="regular" />
              <Radio label="Euro Pallet" value="euro" />
            </RadioGroup>
          </FormGroup>
          {euroQuotaError && (
            <Callout intent="danger" icon="error" className="mt-3 text-sm">
              <div className="font-600 mb-1">Cannot set as Euro pallet</div>
              {euroQuotaError.map((msg, i) => (
                <div key={i}>{msg}</div>
              ))}
              <div className="mt-1">
                Reduce the assigned MH codes / gate passes to the quota or less.
              </div>
            </Callout>
          )}
        </div>
      </CustomDialog>

      {/* === Rejection Dialog === */}
      <CustomDialog
        light
        width="60%"
        icon="cross-circle"
        title={`Rejection - Pallet ${pallet?.palletNumber ?? index + 1}`}
        footerShowCloseButton={false}
        isOpen={isRejectOpen}
        onClose={() => setIsRejectOpen(false)}
        footerRightButton={
          <div className="flex items-center justify-between w-full">
            <CustomButton
              text="Cancel"
              onClick={() => setIsRejectOpen(false)}
            />
            <CustomButton
              intent="danger"
              disabled={production?.sendForApproval}
              text="Confirm Rejection"
              onClick={() => handleRejectConfirm()}
            />
          </div>
        }
      >
        <div className="p-4 space-y-4 text-sm text-gray-700">
          {pallet?.gatePasses?.length ? (
            <>
              <div className="p-2 bg-slate-100 border rounded-lg">
                {(() => {
                  const totalWeight = Object.values(rejectionStats).reduce(
                    (sum, s) => sum + (s.totalWeight + s.totalRejected),
                    0
                  )
                  const totalRejected = Object.values(rejectionStats).reduce(
                    (sum, s) => sum + s.totalRejected,
                    0
                  )
                  const overallPercent =
                    totalWeight > 0
                      ? ((totalRejected / totalWeight) * 100).toFixed(2)
                      : "0.00"

                  const missingWeight = totalRejected.toFixed(0)
                  const missingBoxes = (
                    totalRejected /
                    (production?.shipmentPlan?.skuType?.capacity || 1)
                  ).toFixed(0)

                  // === Color logic ===
                  const percentValue = parseFloat(overallPercent)
                  let colorClass = {
                    text: "text-gray-600",
                    bg: "bg-gray-200",
                    border: "border-gray-400",
                  }

                  if (percentValue > 25)
                    colorClass = {
                      text: "text-red-700",
                      bg: "bg-red-100",
                      border: "border-red-400",
                    }
                  else if (percentValue > 18)
                    colorClass = {
                      text: "text-yellow-700",
                      bg: "bg-yellow-100",
                      border: "border-yellow-400",
                    }
                  else if (percentValue > 7)
                    colorClass = {
                      text: "text-green-700",
                      bg: "bg-green-100",
                      border: "border-green-400",
                    }
                  else
                    colorClass = {
                      text: "text-orange-600",
                      bg: "bg-orange-100",
                      border: "border-orange-400",
                    }

                  return (
                    <div className="flex flex-col gap-1 text-sm">
                      <div className="flex items-center justify-between">
                        <div className={`font-semibold text-lg`}>
                          Rejection:{" "}
                          <span
                            className={`font-bold text-lg ${colorClass.text}`}
                          >
                            {overallPercent}%
                          </span>
                        </div>
                        <CustomTag
                          minimal={false}
                          className={`text-sm font-semibold px-3 py-1 rounded-md ${colorClass.bg} ${colorClass.text} border ${colorClass.border}`}
                        >
                          {`${totalRejected.toFixed(
                            0
                          )}kg | ${missingBoxes} missing box`}
                        </CustomTag>
                      </div>

                      <div className="text-xs text-blue-400 italic">
                        Formula: ({missingWeight}kg missing weight ÷{" "}
                        {totalWeight?.toFixed}kg assigned weight) × 100 ={" "}
                        {overallPercent}%.
                      </div>
                    </div>
                  )
                })()}
              </div>

              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="p-2">Gate Pass</th>
                      <th className="p-2">Variety</th>
                      <th className="p-2 text-right">Assigned (kg)</th>
                      <th className="p-2 text-right">Rejected (kg)</th>
                      <th className="p-2 text-right">Rejection %</th>
                      <th className="p-2 text-right">Packed Boxes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pallet.gatePasses.map((gp, idx) => {
                      const stat = rejectionStats[gp.id] ?? {
                        totalWeight: 0,
                        totalRejected: 0,
                        percent: 0,
                      }

                      const totalAssigned =
                        stat.totalWeight + stat.totalRejected

                      return (
                        <tr key={gp.id ?? idx} className="border-t">
                          <td className="p-2">{gp.gatePass}</td>
                          <td className="p-2">{gp.variety || "--"}</td>
                          <td className="p-2 text-right">
                            {totalAssigned.toFixed(2)}
                          </td>
                          <td className="p-2 text-right text-red-600">
                            {stat.totalRejected.toFixed(2)}
                          </td>
                          <td
                            className={`p-2 text-right font-semibold ${getPercentColor(
                              stat.percent
                            )}`}
                          >
                            {stat.percent.toFixed(1)}%
                          </td>
                          <td className="p-2 text-right flex justify-end">
                            {(() => {
                              const capacity =
                                production?.shipmentPlan?.skuType?.capacity ?? 1
                              const totalWeight = gp.weight ?? 0
                              const rejectedWeight = gp.rejectedWeight ?? 0
                              const totalBoxes =
                                capacity > 0
                                  ? (totalWeight + rejectedWeight) / capacity
                                  : 0

                              return (
                                <InputGroup
                                  type="number"
                                  min="0"
                                  max={totalBoxes.toFixed(2)}
                                  step="0.01"
                                  disabled={production?.sendForApproval}
                                  className="w-28"
                                  placeholder={`Max: ${totalBoxes.toFixed(2)}`}
                                  value={rejectInputs[gp.id]?.toString() ?? ""}
                                  onChange={e =>
                                    setRejectInputs(prev => ({
                                      ...prev,
                                      [gp.id]: Number(e.target.value),
                                    }))
                                  }
                                />
                              )
                            })()}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="text-gray-500 italic">
              No gate passes assigned to this pallet.
            </p>
          )}
        </div>
      </CustomDialog>

      {/* === Reverse Stock Dialog === */}
      <CustomDialog
        light
        width="40%"
        icon="undo"
        title={`Reverse Stock - Pallet ${pallet?.palletNumber ?? index + 1}`}
        isOpen={isReverseStockOpen}
        onClose={() => setIsReverseStockOpen(false)}
        footerShowCloseButton={false}
        footerRightButton={
          <div className="flex items-center justify-between w-full">
            <CustomButton
              text="Close"
              onClick={() => setIsReverseStockOpen(false)}
            />
          </div>
        }
      >
        <div>
          <div className="flex gap-3 justify-between">
            {/* Reverse Stock MH Codes */}
            {pallet?.mhCodes?.length > 0 && (
              <div className="border rounded-lg p-4 w-full bg-gray-300">
                <h6 className="font-semibold mb-2">MH Codes</h6>
                <p className="text-xs text-gray-600 mb-3">
                  {pallet.mhCodes.length} MH code(s) assigned:{" "}
                  {pallet.mhCodes.map(m => m.mhCode).join(", ")}
                </p>
                <ButtonMutation
                  mutation={RESTOCK_PRODUCTION}
                  variables={{
                    input: {
                      lineTradeProfileIds: pallet.mhCodes
                        .map(m => m.responseId)
                        .filter(Boolean),
                    },
                  }}
                  intent="warning"
                  text="Reverse Stock MH Codes"
                  onCompleted={() => {
                    setIsReverseStockOpen(false)
                    if (onRefetch) onRefetch()
                  }}
                />
              </div>
            )}

            {/* Reverse Stock Gate Passes */}
            {pallet?.gatePasses?.length > 0 && (
              <div className="border rounded-lg p-4 w-full bg-gray-300">
                <h6 className="font-semibold mb-2">Gate Passes</h6>
                <p className="text-xs text-gray-600 mb-3">
                  {pallet.gatePasses.length} gate pass(es) assigned:{" "}
                  {pallet.gatePasses.map(g => g.gatePass).join(", ")}
                </p>
                <ButtonMutation
                  mutation={RESTOCK_PRODUCTION}
                  variables={{
                    input: {
                      linePackingAllotmentIds: pallet.gatePasses
                        .map(g => g.responseId)
                        .filter(Boolean),
                    },
                  }}
                  intent="warning"
                  text="Reverse Stock Gate Passes"
                  onCompleted={() => {
                    setIsReverseStockOpen(false)
                    if (onRefetch) onRefetch()
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </CustomDialog>
      {/* === Label Preview Dialog === */}
      <WidgetLabelPreview
        isOpen={isLabelPreviewOpen}
        onClose={() => setIsLabelPreviewOpen(false)}
        labelType={activeLabelType}
        pallet={pallet}
        production={production}
      />
    </>
  )
}
